/**
 * The words used on the customer path.
 *
 * Contract and implementation names stay unchanged at the API boundary. UI
 * copy imports this map so a state or product concept cannot acquire a second
 * name on another screen.
 */
export const CUSTOMER_TERMS = {
  document: { one: "document", many: "documents", title: "Document" },
  memory: { one: "memory", title: "Memory" },
  model: { one: "model", many: "models", title: "Model" },
  version: { one: "version", many: "versions", title: "Version" },
  playground: { one: "playground", title: "Playground" },
  deployment: { one: "deployment", many: "deployments", title: "Deployment" },
  states: {
    waiting: "Waiting",
    waitingForGpu: "Getting a machine ready",
    preparing: "Preparing",
    inProgress: "In progress",
    ready: "Ready",
    partlyReady: "Partly ready",
    didNotFinish: "Did not finish",
    stopped: "Stopped",
    archived: "Archived",
    unavailable: "Not available",
  },
} as const;

/** Static rendered-copy checks use the same deny-list as the vocabulary map. */
export const FORBIDDEN_SIMPLE_PATH_TERMS = [
  { name: "Talk", pattern: /\bTalk\b/ },
  { name: "GPU", pattern: /\bgpu\b/i },
  { name: "pool", pattern: /\bpool\b/i },
  { name: "sandbox", pattern: /\bsandbox\b/i },
  { name: "quarantine", pattern: /\bquarantine\w*\b/i },
  { name: "digest", pattern: /\bdigest\w*\b/i },
  { name: "revision", pattern: /\brevisions?\b/i },
  { name: "r1", pattern: /\br\d+\b/i },
  { name: "projection", pattern: /\bprojections?\b/i },
  { name: "chunk", pattern: /\bchunks?\b|\bchunking\b/i },
  { name: "run generation", pattern: /\brun\s+g\d+\b/i },
  { name: "exact_extract", pattern: /\bexact_extract\b/i },
  { name: "admitted_bytes", pattern: /\badmitted_bytes\b/i },
  { name: "bps", pattern: /\bbps\b/i },
  { name: "Advanced", pattern: /\badvanced\b/i },
  { name: "stored-flag explanation", pattern: /it is not a stored flag/i },
] as const;

/**
 * File-level exceptions are limited to surfaces whose purpose is legal text,
 * API inspection, or an explicitly advanced configuration/detail flow. A
 * `<Disclosure>` block is exempt locally without exempting its whole file.
 */
export const ADVANCED_DETAIL_TEXT_PATHS = [
  /^pages\/(?:api-reference|privacy|terms)\.vue$/,
  /^components\/citation\//,
  /^components\/chat\/ChatCitations\.vue$/,
  /^components\/deployments\/DeploymentScalingTab\.vue$/,
  /^components\/data\/DatasetRevisionsSection\.vue$/,
  /^components\/data-objects\/(?:DataObjectAdmissionCard|DataObjectRunsCard|DataObjectUsageTable)\.vue$/,
  /^components\/deploy\/DeploymentsSection\.vue$/,
  /^components\/knowledge-base\/KnowledgeBaseCreateForm\.vue$/,
  /^components\/models\/(?:ResolvedLoraConfigCard|TrainCostEstimate|TrainingExecutionsChain)\.vue$/,
  /^components\/training\/(?:TrainingRunConfigPreview|TrainingRunMonitor)\.vue$/,
  /^pages\/knowledge-bases\/\[id\]\.vue$/,
] as const;
